import { useEffect, useState } from "react";
// import { Col } from "reactstrap";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Nav, NavItem, UncontrolledTooltip } from "reactstrap";
import { SidebarLinks } from "./SidebarLinks";
import Logo from "../../../assets/images/logo.jpeg";
import packageJson from "../../../../package.json";
import { getBackendVersion } from "../../../services/dashboard.service";
import { useAbility } from "../../../contexts/AbilityContext";
/**
 * Sidebar Component for the app
 *
 * @returns {JSX.Element}
 */
export default function Sidebar() {
  const ability = useAbility();
  const sidebarMenu = SidebarLinks.filter((item) => ability.can("read", item.subject));
  const location = useLocation();
  const frontendVersion = packageJson.version;
  const [backendVersion, setBackendVersion] = useState("N/A");

  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    const syncCollapsedState = () => {
      const sidebarElement = document.querySelector(".sidebarMenu");
      setIsCollapsed(
        document.body.classList.contains("sidebarAction") ||
          sidebarElement?.classList.contains("sidebarAction") ||
          false,
      );
    };

    syncCollapsedState();

    const observer = new MutationObserver(syncCollapsedState);
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    const sidebarElement = document.querySelector(".sidebarMenu");
    if (sidebarElement) {
      observer.observe(sidebarElement, { attributes: true, attributeFilter: ["class"] });
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    getBackendVersion()
      .then((response: any) => {
        setBackendVersion(response?.data?.version || "N/A");
      })
      .catch(() => {
        setBackendVersion("N/A");
      });
  }, []);

  const toggleDropdown = (dropdownId: any) => {
    if (openDropdownId === dropdownId) {
      setOpenDropdownId(null);
    } else {
      setOpenDropdownId(dropdownId);
    }
  };

  const renderSidebarItem = (item: any) => {
    const { id, menuName, menuIcon, path, children } = item;
    const parentTooltipId = `sidebar-item-${id}`;
    if (children) {
      const isDropdownOpen = openDropdownId === id;
      const dropdownClass = isDropdownOpen ? "show" : "";

      const isChildActive = children.some((child: any) => location.pathname.startsWith(child.path));

      return (
        <NavItem as='li' key={id}>
          <NavItem
            id={parentTooltipId}
            role='button'
            tabIndex={0}
            className={`align-items-center d-flex fw-medium nav-link px-0 sidebarLink text-nowrap ${
              isChildActive ? "active" : ""
            }`}
            onClick={() => toggleDropdown(id)}
            aria-expanded={isDropdownOpen ? "true" : "false"}
          >
            <span className='mx-1 py-1 text-center px-3 sidebarIcon'>{menuIcon}</span>
            <span className='hideInSmallSidebar'>{menuName}</span>
          </NavItem>

          {isCollapsed && (
            <UncontrolledTooltip placement='right' target={parentTooltipId} container='body'>
              {menuName}
            </UncontrolledTooltip>
          )}

          <Nav as='ul' id={`submenu-${id}`} className={`flex-column collapse ${dropdownClass}`}>
            {children.map((child: any) => {
              const {
                id: childId,
                menuName: childMenuName,
                menuIcon: childMenuIcon,
                path: childPath,
              } = child;
              const childTooltipId = `sidebar-child-${childId}`;

              return (
                <NavItem as='li' key={childId} className='ms-3'>
                  <NavLink
                    id={childTooltipId}
                    to={childPath}
                    className='align-items-center d-flex fw-medium nav-link px-0 sidebarLink text-nowrap'
                  >
                    <span className='mx-1 py-1 text-center px-3 sidebarIcon'>{childMenuIcon}</span>
                    <span className='hideInSmallSidebar'>{childMenuName}</span>
                  </NavLink>

                  {isCollapsed && (
                    <UncontrolledTooltip placement='right' target={childTooltipId} container='body'>
                      {childMenuName}
                    </UncontrolledTooltip>
                  )}
                </NavItem>
              );
            })}
          </Nav>
        </NavItem>
      );
    } else {
      const itemTooltipId = `sidebar-item-${id}`;
      return (
        <NavItem as='li' key={id}>
          <NavLink
            id={itemTooltipId}
            to={path}
            className='align-items-center d-flex fw-medium nav-link px-0 sidebarLink text-nowrap'
          >
            <span className='mx-1 py-1 text-center px-3 sidebarIcon'>{menuIcon}</span>
            <span className='hideInSmallSidebar'>{menuName}</span>
          </NavLink>

          {isCollapsed && (
            <UncontrolledTooltip placement='right' target={itemTooltipId} container='body'>
              {menuName}
            </UncontrolledTooltip>
          )}
        </NavItem>
      );
    }
  };

  return (
    <div className='sidebarMenu h-100'>
      <aside className='h-100 bg-white'>
        <div className='d-flex flex-column h-100'>
          <div className='align-items-center d-flex justify-content-center p-2 sidebarLogo text-center'>
            <Link to='/' className='d-inline-block py-1 px-2'>
              <img src={Logo} alt='Logo' className='img-fluid' width={160} />
            </Link>
          </div>
          <div className='d-flex flex-column justify-content-between h-100'>
            <div className='overflow-auto sidebarList'>
              <Nav as='ul' className='flex-column'>
                {sidebarMenu.map((elem) => renderSidebarItem(elem))}
              </Nav>
            </div>
            <div className='sidebarVersionInfo hideInSmallSidebar'>
              <span>
                Backend: <strong>{backendVersion}</strong>
              </span>
              <span>
                Frontend: <strong>{frontendVersion}</strong>
              </span>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
